import { ApolloQueryResult } from '@apollo/client';
import { EventType } from '@components/Event';
import EventPreview from '@components/Event/EventPreview';
import LivestreamDataProvider from '@components/Event/provider/LivestreamDataProvider';
import StreamPresenceDataProvider from '@components/Event/provider/StreamPresenceDataProvider';
import FullLoader from '@components/FullLoader';
import Layout from '@components/Layout';
import LivestreamPlayer from '@components/Player/LivestreamPlayer';
import { useMicrositeConfig } from '@components/Site/provider/MicrositeConfigProvider';
import PrivacyPolicyModal from '@components/User/PrivacyPolicyModal';
import UserEfnNumberModal from '@components/User/UserEfnNumberModal';
import {
    GetEventDocument,
    GetEventQuery,
    GetMicrositeDocument,
    GetMicrositeQuery
} from '@graphql/hasura';
import { fetchSiteConfig, withApollo } from '@lib/apollo';
import { createApolloClient } from '@lib/apollo/createApolloClient';
import { useFirebaseApp, useLivestreamOnAir, useLivestreamSource } from '@lib/firebase';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';

const Index: NextPage<{ eventData: GetEventQuery }> = ({ eventData }) => {
    const event = eventData.events_by_pk as EventType;
    const { user, userData, userIsLoading } = useFirebaseApp();
    const [livestream_source] = useLivestreamSource(event.id);
    const [onair] = useLivestreamOnAir(event.id);
    const router = useRouter();
    const { site } = useMicrositeConfig();
    const requireEfnNumberFromUser =
        event.require_efn_number &&
        userData &&
        userData.account_type === 'participant' &&
        !userData.efn_number;
    const requirePrivacyPolicy =
        event.country === 'UK' &&
        userData &&
        site.name === 'fomf-webup-site' &&
        !userData.accepted_privacy_policy;

    useEffect(() => {
        if (event.ondemand_redirect) {
            setTimeout(
                () =>
                    router.push(`/events/${event.id}/on-demand`, undefined, {
                        locale: router.locale
                    }),
                100
            );
        }
    });

    if (userIsLoading || event.ondemand_redirect) {
        return <FullLoader />;
    }
    return (
        <Layout event={event}>
            <Head>
                <title>Livestream - {event.title}</title>
            </Head>
            <LivestreamDataProvider event={event}>
                <StreamPresenceDataProvider event={event}>
                    {requirePrivacyPolicy ? <PrivacyPolicyModal /> : null}
                    {requireEfnNumberFromUser ? <UserEfnNumberModal /> : null}
                    {user && livestream_source && onair?.stream && !requireEfnNumberFromUser ? (
                        <LivestreamPlayer event={event} url={livestream_source.url} />
                    ) : (
                        <EventPreview event={event} />
                    )}
                </StreamPresenceDataProvider>
            </LivestreamDataProvider>
        </Layout>
    );
};

export const getStaticPaths: GetStaticPaths = async () => {
    const apolloClient = createApolloClient({}, true);
    const { data }: ApolloQueryResult<GetMicrositeQuery> = await apolloClient.query({
        query: GetMicrositeDocument,
        variables: {
            id: process.env.NEXT_PUBLIC_MICROSITE_UID
        }
    });

    const events = data.microsites_by_pk?.events || [];
    const paths = events.map((event) => {
        return {
            params: {
                id: event.event_id
            },
            locale: event.event.locale_code
        };
    });

    // We'll pre-render only these paths at build time.
    // { fallback: false } means other routes should 404.
    return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
    const apolloClient = createApolloClient({}, true);
    const { data }: ApolloQueryResult<GetEventQuery> = await apolloClient.query({
        query: GetEventDocument,
        variables: { id: ctx.params?.id }
    });

    if (data.events_by_pk) {
        return {
            props: { eventData: data, site: await fetchSiteConfig() }, // will be passed to the page component as props

            // Next.js will attempt to re-generate the page:
            // - When a request comes in
            // - At most once every second
            revalidate: 60 // In seconds
        };
    }

    return {
        notFound: true,
        revalidate: 60 // In seconds
    };
};

export default withApollo()(Index);
